import React, { memo, useEffect, useState } from "react";
import { Form } from "antd";
import Pagination from "../../../BP2Components/Pagination";
import { Empty, Message, Spin } from "../../../BP2Components";
import { renderDevInfo } from "../../GeneralScripts";
import "./QuestionnaireTableField.less";
import ChooseOneField from "./QuestionnaireTableTypesOfFields/ChooseOneField";
import { getListV2 } from "../../../../services/api/Api";
import { createContent } from "./questionnarieTableScripts";
import { useIntl } from "react-intl";
import { handleGridTechname } from "../../GeneralScripts";
import PutInOrderField from "./QuestionnaireTableTypesOfFields/PutInOrderField";
import MultiChooseFromField from "./QuestionnaireTableTypesOfFields/MultiChooseFromField";
import { useFormContext } from "../../../../context/FormContext/FormContext";
import { saveFormV2 } from "../../../../services/restAPI/RestApi";
import { useSelector } from "react-redux";
import { transformFormValuesForRequest } from "../../GeneralScripts";
import projectConfig from "../../../../projectConfig.json";
import { store } from "../../../../appRedux/store/Store";
import moment from "moment";

export interface QuestionnaireTableQuestion {
   questionId: number;
   questionnaireId?: number;
   type: string;
   content: string;
   hint?: string | null;
   options?: string[] | null;
   additionalAnswer?: boolean | null;
   maxPoints?: number | null;
   description?: string | null;
   order: number | null;
   requiredAnswers?: number | null;
   correctAnswer?: string | null;
}

//Those have specific names, because they are techName used in saveForm
export interface QuestionnaireTableAnswer {
   questionnaire_id: number;
   question_id: number;
   question_type: string;
   question_content: string;
   answer: string;
   surveyed_user_id: number;
   compared_user_id: number;
   received_points: number | null | undefined;
   max_points: number | null | undefined;
   question_description: string | null | undefined;
}

interface QuestionnaireTableFieldProps {
   element: any;
   formUuid: number;
   form: any; // antd Form instance
}

interface QuestionnaireContent extends QuestionnaireTableQuestion {
   fields: QuestionnaireTableQuestion[];
}

const QuestionnaireTableField: React.FC<QuestionnaireTableFieldProps> = ({
   form,
   element,
   formUuid,
}) => {
   const [loading, setLoading] = useState(true);
   const [page, setPage] = useState(1);
   const [content, setContent] = useState<QuestionnaireContent[]>([]);
   const intl = useIntl();
   const { user } = useSelector((state) => state);
   const { formData, changeFieldPendingStatus, addSaveCallback, setFormData } = useFormContext();

   const handleQuestionnaireTableSave = async (questionnaireId: string) => {
      try {
         //@ts-ignore
         const currentFormInfo = store.getState()?.formsv2.formInfo;
         const questionnaireTableAnswers = currentFormInfo[formUuid]?.questionnaireTableAnswers;

         const questionnaireName = formData
            ? Object.values(formData.forms_data.tab[0].elements).find(
                 (element: any) => element.tech_name === "survey_title",
                 //@ts-ignore
              )?.value
            : "";

         const questionnaireFormBody = {
            surveyed_user_id: user.oldId,
            Answer_ssr_updated_jxtjsp: moment().format("YYYY-MM-DD HH:mm:ss"),
            compared_user_id: "",
            question_content: questionnaireName,
         };

         if (!formUuid && Object.values(questionnaireTableAnswers).length > 0) return true;
         const answers = Object.values(questionnaireTableAnswers);

         const pointsTotal = answers.reduce((acc, answer: any) => {
            return acc + answer.received_points;
         }, 0);

         const questionnaireRes = await saveFormV2(
            projectConfig.questionnaireTable.saveQuestionnaireForm,
            null, //formKey
            {
               ...questionnaireFormBody,
               points: pointsTotal,
               questionType: "30009907",
               question_id: questionnaireId,
            },
         );

         const answeredQuestionnaireId =
            questionnaireRes?.data?.default?.recordInfo?.[0]?.id ?? null;

         await Promise.all(
            answers.map((answer) => {
               const dataQuestionnaire = transformFormValuesForRequest(answer);
               return saveFormV2(
                  projectConfig.questionnaireTable.saveQuestionForm,
                  null, //noFormKey - support
                  {
                     ...dataQuestionnaire,
                     Answer_answered_questionnaire_id_1ijreq: answeredQuestionnaireId,
                  },
               );
            }),
         );

         await saveFormV2(
            projectConfig.questionnaireTable.saveQuestionnaireForm,
            null, //noFormKey - support
            {
               ...questionnaireFormBody,
               points: pointsTotal,
               questionType: "30009909",
               question_id: questionnaireId,
            },
         );

         return true;
      } catch (err) {
         Message.destroy();
         Message.error(intl.formatMessage({ id: "messageModal.ErrorWhileSavingQuestionnarie" }));
         return false;
      }
   };

   const getFormDataWithSubmitVisibility = (visible: boolean) => ({
      ...formData,
      forms_data: {
         ...formData.forms_data,
         hide_submit: visible ? "0" : "1",
      },
   });

   useEffect(() => {
      setFormData(getFormDataWithSubmitVisibility(false));
      setLoading(true);
      addSaveCallback(async (dataId, res) => {
         const questionnaireId = res?.data?.default?.recordInfo?.[0]?.id;
         Message.loading(intl.formatMessage({ id: "form.isProcessing" }));
         await handleQuestionnaireTableSave(questionnaireId);
         Message.destroy();
      });
      changeFieldPendingStatus && changeFieldPendingStatus(true);

      const filters = (element?.listTableFilters || []).map((listFilter) => {
         const fieldValue = form.getFieldValue(listFilter.main_form_field_to_filter_techname);
         let value = fieldValue;
         if (fieldValue && typeof fieldValue === "object") {
            value = fieldValue.value ?? fieldValue.id ?? "";
         }
         return {
            columnTechname: listFilter.list_form_field_to_filter_techname,
            value,
            isStrict: listFilter.is_strict,
         };
      });

      getListV2(element.num1, 1, 1000, filters)
         .then((res) => {
            setContent(createContent(res.data.default));
            setFormData(getFormDataWithSubmitVisibility(true));
         })
         .catch((err) => {
            Message.error(intl.formatMessage({ id: "form.errorWhileLoadingQuestionnaire" }));
         })
         .finally(() => {
            setLoading(false);
            changeFieldPendingStatus && changeFieldPendingStatus(false);
         });
   }, []);

   return (
      <Form.Item
         key={element.tech_name}
         className={`${element?.type}-field`}
         style={{
            gridArea: handleGridTechname(element.tech_name),
         }}
      >
         {loading ? (
            <div className="spin-middle">
               <Spin />
            </div>
         ) : content.length === 0 ? (
            <Empty />
         ) : (
            <>
               <div className="questionnaire-table-container">
                  <div className="questionnaire-table-container__fields">
                     {content[page - 1].fields.map((field) => {
                        switch (field?.type) {
                           case "chooseOne":
                              return (
                                 <ChooseOneField
                                    question={field}
                                    formUuid={formUuid}
                                    key={field.questionId}
                                 />
                              );
                           //Mysi sprzęt który pomoże Nam potem
                           // case "SimpleQuestion":
                           //    return (
                           //       <p>
                           //          {field.type} {field.content}
                           //       </p>
                           //    );
                           case "multiChooseFrom":
                              return (
                                 <MultiChooseFromField
                                    question={field}
                                    formUuid={formUuid}
                                    key={field.questionId}
                                 />
                              );
                           case "putInOrder":
                              return (
                                 <PutInOrderField
                                    question={field}
                                    formUuid={formUuid}
                                    key={field.questionId}
                                 />
                              );
                           default:
                              return null;
                        }
                     })}
                  </div>
                  <Pagination
                     initialPage={1}
                     customClassName="questionnaire-table-container__pagination"
                     defaultPageSize={1}
                     totalItems={content.length}
                     onChange={(clickedPage) => setPage(clickedPage)}
                  />
               </div>

               {renderDevInfo(element)}
            </>
         )}
      </Form.Item>
   );
};

export default memo(QuestionnaireTableField);
